import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, ArrowLeft, Banknote, Clock, Search, Users } from "lucide-react";
import { Reveal } from "../components/common";
import { SOLUTIONS } from "../data/content";
import { LANDING } from "../data/landing";
import { Seo, SITE_URL, ORG_REF, breadcrumbs } from "../components/Seo";
import { FaqSection } from "../components/FaqSection";
import { QuoteForm } from "../components/QuoteForm";
import { RelatedGuides } from "../components/RelatedGuides";
import { SOLUTION_FAQS, faqJsonLd } from "../data/faqs";

const LEGACY_SLUGS = { "vehicle-finance": "asset-finance", "equipment-finance": "asset-finance", "working-capital": "business-loans" };

const scrollToQuote = () => document.getElementById("quote")?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function ServicePage() {
  const { slug } = useParams();
  const service = SOLUTIONS.find((s) => s.slug === slug);
  const page = LANDING[slug];
  if (LEGACY_SLUGS[slug]) return <Navigate to={`/solutions/${LEGACY_SLUGS[slug]}`} replace />;
  if (!service || !page) {
    return (
      <div className="pt-40 pb-32 container-x mx-auto max-w-[1400px] text-center">
        <h1 className="text-4xl font-black">Solution not found</h1>
        <Link to="/solutions" className="text-lime mt-6 inline-block">Back to solutions</Link>
      </div>
    );
  }
  const Icon = service.icon;
  const others = SOLUTIONS.filter((s) => s.slug !== slug).slice(0, 3);
  const url = `${SITE_URL}/solutions/${service.slug}`;

  return (
    <div data-testid="service-page" className="pt-32">
      <Seo
        title={page.h1}
        path={`/solutions/${service.slug}`}
        description={`${page.sub} ${service.detail}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Service", "@id": `${url}#service`, name: service.title, serviceType: service.title, description: `${page.sub} ${service.detail}`, url, provider: ORG_REF, areaServed: { "@type": "Country", name: "United Kingdom" }, audience: { "@type": "BusinessAudience", name: "UK businesses" }, keywords: page.keywords },
            breadcrumbs([{ name: "Home", path: "/" }, { name: "Solutions", path: "/solutions" }, { name: service.title, path: `/solutions/${service.slug}` }]),
            faqJsonLd(SOLUTION_FAQS[service.slug] || []),
          ],
        }}
      />

      {/* HERO */}
      <section className="container-x mx-auto max-w-[1400px] py-16">
        <Link to="/solutions" className="inline-flex items-center gap-2 text-grey hover:text-lime mb-10 text-sm"><ArrowLeft className="w-4 h-4" /> All solutions</Link>
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <Icon className="w-16 h-16 text-lime mb-8" strokeWidth={1.5} />
            <h1 data-testid="landing-h1" className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">{page.h1}</h1>
            <p className="text-grey text-lg mt-6 leading-relaxed">{page.sub}</p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <button onClick={scrollToQuote} data-testid="landing-hero-cta" className="inline-flex items-center justify-center gap-2 bg-lime text-[#1A1A1A] font-bold px-8 py-4 hover:glow-lime transition-shadow">{page.cta} <ArrowRight className="w-5 h-5" /></button>
              <Link to="/booking" className="inline-flex items-center justify-center border border-white/30 font-semibold px-8 py-4 hover:border-lime hover:text-lime transition-colors">Book a Consultation</Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-[#808080]/30 p-8 md:p-10">
              <h2 className="text-xl font-bold mb-6">{page.finance.title}</h2>
              <ul className="grid sm:grid-cols-2 gap-4">
                {page.finance.items.map((f) => (
                  <li key={f} className="flex items-start gap-3"><Check className="w-5 h-5 text-lime shrink-0 mt-0.5" /><span className="text-white/90">{f}</span></li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* KEY FACTS */}
      <section className="border-t border-[#808080]/20">
        <div className="container-x mx-auto max-w-[1400px] py-20">
        <div className="grid md:grid-cols-3 gap-px bg-[#808080]/20 border border-[#808080]/20">
          <Reveal className="bg-[#1A1A1A] p-8 md:p-10 h-full">
            <Users className="w-8 h-8 text-lime mb-6" strokeWidth={1.5} />
            <h2 className="text-xl font-bold">Who can we help?</h2>
            <ul className="mt-5 space-y-3">
              {page.help.map((h) => <li key={h} className="flex items-start gap-3 text-grey text-sm leading-relaxed"><span className="w-1.5 h-1.5 bg-lime mt-2 shrink-0" />{h}</li>)}
            </ul>
          </Reveal>
          <Reveal delay={0.08} className="bg-[#1A1A1A] p-8 md:p-10 h-full">
            <Banknote className="w-8 h-8 text-lime mb-6" strokeWidth={1.5} />
            <h2 className="text-xl font-bold">How much can you borrow?</h2>
            <p data-testid="landing-borrow" className="text-3xl font-black text-lime tracking-tight mt-5">{page.borrow.headline}</p>
            <p className="text-grey text-sm leading-relaxed mt-4">{page.borrow.text}</p>
          </Reveal>
          <Reveal delay={0.16} className="bg-[#1A1A1A] p-8 md:p-10 h-full">
            <Clock className="w-8 h-8 text-lime mb-6" strokeWidth={1.5} />
            <h2 className="text-xl font-bold">How quickly can funding be arranged?</h2>
            <p className="text-3xl font-black text-lime tracking-tight mt-5">{page.speed.headline}</p>
            <p className="text-grey text-sm leading-relaxed mt-4">{page.speed.text}</p>
          </Reveal>
        </div>
        </div>
      </section>

      {/* LENDERS */}
      <section className="container-x mx-auto max-w-[1400px] py-24 grid lg:grid-cols-[1fr_1.6fr] gap-12 lg:gap-20">
        <Reveal>
          <Search className="w-10 h-10 text-lime mb-6" strokeWidth={1.5} />
          <span className="text-xs uppercase tracking-[0.3em] text-lime">Underwriting</span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-4">What do lenders look for?</h2>
          <p className="text-grey text-lg mt-6 max-w-md">Knowing what underwriters weigh up lets us present your case properly the first time, which means faster decisions and better terms.</p>
        </Reveal>
        <Reveal delay={0.1}>
          <ol className="border-t border-[#808080]/30">
            {page.lenders.map((l, i) => (
              <li key={l} className="flex items-start gap-6 py-5 border-b border-[#808080]/30">
                <span className="text-lime font-black text-lg tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-white/90 text-base md:text-lg">{l}</span>
              </li>
            ))}
          </ol>
        </Reveal>
      </section>

      {SOLUTION_FAQS[service.slug] && <FaqSection faqs={SOLUTION_FAQS[service.slug]} title={`${service.title} questions`} />}

      {/* QUOTE */}
      <section id="quote" data-testid="landing-quote-section" className="border-t border-[#808080]/20 bg-black scroll-mt-28">
        <div className="container-x mx-auto max-w-[1400px] py-24 grid lg:grid-cols-5 gap-16">
          <Reveal className="lg:col-span-2">
            <span className="text-xs uppercase tracking-[0.3em] text-lime">Get a quote</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4">{page.cta}</h2>
            <p className="text-grey text-lg mt-6 leading-relaxed">Tell us a little about your business and requirement. We'll compare {service.title.toLowerCase()} options from across 165+ UK lenders and come back to you with clear, tailored terms. No obligation, no impact on your credit score.</p>
            <ul className="mt-8 space-y-3">
              {["Whole-of-market comparison", "Dedicated relationship manager", "Plain-English advice", "No obligation quote"].map((f) => (
                <li key={f} className="flex items-center gap-3 text-white/90"><Check className="w-5 h-5 text-lime shrink-0" />{f}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-3">
            <QuoteForm source={`landing-${service.slug}`} presetType={service.title} submitLabel={page.cta} successText={`Your ${service.title.toLowerCase()} enquiry has been received. A DNA Finance adviser will contact you shortly.`} />
          </Reveal>
        </div>
      </section>

      <RelatedGuides slug={service.slug} title={service.title} />

      <section className="container-x mx-auto max-w-[1400px] py-16 border-t border-[#808080]/20">
        <h2 className="text-2xl font-bold mb-10">Other funding solutions</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {others.map((s) => (
            <Link key={s.slug} to={`/solutions/${s.slug}`} className="group border border-[#808080]/20 p-6 hover:border-lime/40 transition-colors">
              <s.icon className="w-8 h-8 text-lime mb-4" strokeWidth={1.5} />
              <h3 className="font-bold group-hover:text-lime transition-colors">{s.title}</h3>
              <p className="text-grey text-sm mt-2">{s.blurb}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
